import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import {
	CustomDropdown,
	adminInitiateMigration,
	adminGetMigrationStatus,
	adminGetMigrationJobs,
	adminUpdateClient,
	clientApi,
} from "shared"
import type { MigrationInitiateResponse } from "shared/src/types/migration.types"
import React, { useState, useEffect } from "react"
// import "./MigrationPage.css"
import type { ClientProps } from "Utils/client.types"
import { Spinner } from "shared/src/components/shadcn/components/ui/spinner"

const ACCOUNT_DATA =
	process.env.VITE_IS_TEST === "true" || process.env.VITE_IS_DEV === "true"
		? [
				{ key: "test", label: "Test", disabledForOrigin: "https://t1-admin.test-hireara.ai" },
				{ key: "dev", label: "Dev", disabledForOrigin: "http://localhost:3001" },
			]
		: [
				{ key: "uk", label: "UK", disabledForOrigin: "https://admin.hireara.ai" },
				{ key: "us", label: "US", disabledForOrigin: "https://admin.us.hireara.ai" },
				{ key: "eu", label: "EU", disabledForOrigin: "https://admin.eu.hireara.ai" },
				{ key: "apac", label: "APAC", disabledForOrigin: "https://admin.apac.hireara.ai" },
			]

export function MigrationPage({
	clientId,
	data,
	setData,
}: Readonly<{
	data: any
	clientId: string
	setData: (data: ClientProps) => void
}>): JSX.Element {
	const [isMigrating, setIsMigrating] = useState<boolean>(false)
	const [selectedAccount, setSelectedAccount] = useState<string>("")
	const [message, setMessage] = useState<string>("")
	const [migrationJob, setMigrationJob] = useState<MigrationInitiateResponse | null>(null)
	const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false)
	const [isLoadingJobs, setIsLoadingJobs] = useState<boolean>(false)
	const [isDisabling, setIsDisabling] = useState<boolean>(false)

	// Fetch migration jobs on component load
	useEffect(() => {
		const fetchMigrationJobs = async () => {
			setIsLoadingJobs(true)
			try {
				const jobs = await adminGetMigrationJobs({ clientId })

				// If jobs exist and no current migration job is set, use the latest one
				if (jobs.length > 0 && !migrationJob) {
					const latestJob = jobs[0] // Jobs are sorted by creation time (most recent first)

					// Convert MigrationJob to MigrationInitiateResponse format for compatibility
					const migrationResponse: MigrationInitiateResponse = {
						job_id: latestJob.jobId,
						client_id: latestJob.clientId,
						jwt_token: "", // Not available from jobs list
						scopes: ["status", "role"], // Default scopes
						expiry_time: latestJob.expiryTime || "",
						status: {
							data_migration: latestJob.dataMigrationStatus,
							file_migration: latestJob.fileMigrationStatus,
							user_migration: latestJob.userMigrationStatus,
						},
						recordType: latestJob.recordType,
					}

					setMigrationJob(migrationResponse)
				}
			} catch (error) {
				console.error("Failed to fetch migration jobs:", error)
			} finally {
				setIsLoadingJobs(false)
			}
		}

		fetchMigrationJobs()
	}, [clientId, migrationJob])

	const handleAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedAccount(event.target.value)
	}

	const handleMigration = async () => {
		if (!selectedAccount) {
			setMessage("Please select an account to migrate to.")
			return
		}

		const targetAccountLabel = ACCOUNT_DATA.find((option) => option.key === selectedAccount)?.label || selectedAccount

		if (
			window.confirm(
				`Migrating data from: \n${data.name}\nTo: ${targetAccountLabel}\n\nThis action will migrate all data. Are you sure?`
			)
		) {
			setIsMigrating(true)
			setMessage("")

			try {
				console.info(`Migration initiated for client ${clientId} to ${selectedAccount}`)

				// Initiate migration using the API
				const migrationResult = await adminInitiateMigration({
					clientId,
					migration_type: "full",
					target: selectedAccount,
					scopes: ["status", "role"],
					additional_params: {
						client_name: data.name,
						initiated_by: "admin_portal",
						timestamp: new Date().toISOString(),
					},
				})

				// Store migration job details for status tracking
				setMigrationJob({ ...migrationResult, recordType: "source" })

				setMessage(
					`Migration to ${targetAccountLabel} initiated successfully!\n` +
						`Job ID: ${migrationResult.job_id}\n` +
						`Status: ${migrationResult.status.data_migration} / ${migrationResult.status.file_migration} / ${migrationResult.status.user_migration}\n` +
						`Expires: ${new Date(migrationResult.expiry_time).toLocaleString()}`
				)

				console.info("Migration job initiated:", migrationResult)
			} catch (error) {
				console.error("Migration initiation failed:", error)
				let errorMessage = "Migration failed. Please try again."

				// Handle specific error types
				if (error?.response?.status === 400) {
					errorMessage = "Invalid migration request. Please check the parameters."
				} else if (error?.response?.status === 403) {
					errorMessage = "Insufficient permissions to initiate migration."
				} else if (error?.response?.status === 404) {
					errorMessage = "Migration service not available."
				} else if (error?.response?.status === 504) {
					errorMessage = "Migration request timed out. Please try again."
				} else if (error?.message) {
					errorMessage = `Migration failed: ${error.message}`
				}

				setMessage(errorMessage)
			} finally {
				setIsMigrating(false)
			}
		}
	}

	const handleCheckStatus = async () => {
		if (!migrationJob) return

		setIsCheckingStatus(true)
		try {
			const statusResult = await adminGetMigrationStatus({
				clientId,
				jobId: migrationJob.job_id,
			})

			// Update the stored migration job with latest status
			setMigrationJob((prev) => (prev ? { ...prev, status: statusResult.status } : prev))

			const recordsMigrated = statusResult.partialDataMigrationStatuses.reduce(
				(total, item) => total + (item.items_migrated || 0),
				0
			)

			const statusMessage =
				`Migration Status Updated:\n` +
				`Data: ${statusResult.status.data_migration}\n` +
				`Files: ${statusResult.status.file_migration}\n` +
				`Users: ${statusResult.status.user_migration}\n` +
				`${statusResult.progress ? `Progress: ${recordsMigrated || 0} records migrated` : ""}`

			setMessage(statusMessage)

			console.log("Migration status:", statusResult)
		} catch (error) {
			console.error("Status check failed:", error)
			let errorMessage = "Failed to check migration status."

			if (error?.response?.status === 404) {
				errorMessage = "Migration job not found. It may have expired."
			} else if (error?.response?.status === 403) {
				errorMessage = "Migration authorization expired. Job may need to be restarted."
			}

			setMessage(errorMessage)
		} finally {
			setIsCheckingStatus(false)
		}
	}

	const handleDisableClient = async () => {
		if (
			window.confirm(
				`Are you sure you want to disable client: ${data.name}?\n\nThis action will disable the client account and prevent access to the system.`
			)
		) {
			setIsDisabling(true)
			setMessage("")

			try {
				await adminUpdateClient({
					clientId,
					updateType: "disable",
					newState: true,
					disabledReason:
						"This account has moved and you can no longer login here, please use your new regional instance instead.",
				})
				setData({ ...data, disabled: true })

				setMessage("Client has been disabled successfully.")
				console.info(`Client ${clientId} has been disabled`)
			} catch (error) {
				console.error("Client disable failed:", error)
				let errorMessage = "Failed to disable client. Please try again."

				if (error?.response?.status === 403) {
					errorMessage = "Insufficient permissions to disable client."
				} else if (error?.response?.status === 404) {
					errorMessage = "Client not found."
				} else if (error?.message) {
					errorMessage = `Disable failed: ${error.message}`
				}

				setMessage(errorMessage)
			} finally {
				setIsDisabling(false)
			}
		}
	}

	function handleToggleSSO() {
		setData({ ...data, ssoEnabled: !data.ssoEnabled })
		clientApi.patch({ clientId, updates: { ssoEnabled: !data.ssoEnabled } }).catch((error) => {
			console.error("Failed to update SSO setting:", error)
		})
	}

	let recordType = "Unknown direction"
	if (migrationJob?.recordType != null) {
		recordType = migrationJob.recordType === "source" ? "Outgoing" : "Incoming"
	}

	return (
		<div className="account-container">
			<h1 className="bs-h1">Account Migration</h1>
			{isLoadingJobs && (
				<div>
					<Spinner></Spinner>Loading migration jobs...
				</div>
			)}
			{message && (
				<div
					style={{
						color: message.includes("failed") ? "var(--ara-error)" : "var(--ara-success, #00C853)",
						fontSize: 18,
						margin: "20px auto",
					}}>
					{message}
				</div>
			)}
			<div
				style={{
					minHeight: 200,
					display: "flex",
					flexDirection: "column",
					justifyContent: "left",
					alignItems: "left",
					marginBottom: 20,
				}}>
				<h4 className="bs-h4">Migrate Account Data</h4>
				{!data.disabled && (
					<>
						<p>The client must be disabled before migration can be started.</p>
						<div style={{ marginBottom: 20 }}>
							<Button
								onClick={handleDisableClient}
								loading={isDisabling}
								disabled={isDisabling}
								style={{
									backgroundColor: "var(--ara-error, #d32f2f)",
									color: "white",
									border: "1px solid var(--ara-error, #d32f2f)",
								}}>
								Disable Client
							</Button>
						</div>
					</>
				)}
				{data.disabled && (
					<>
						<p>Do you want to switch on SSO as part of the migration?</p>
						<div
							style={{
								marginBottom: 20,
								display: "flex",
								alignItems: "center",
								gap: "15px",
								justifyContent: "flex-start",
							}}>
							<div>Enable SSO:</div>
							<div style={{ width: 45 }}>
								<InlineLabelSwitch checked={data.ssoEnabled} onCheckedChange={handleToggleSSO} />
							</div>
						</div>
					</>
				)}
				{data.disabled && (
					<>
						<p>
							Select the target account to migrate all data to. This will transfer all client data, templates, and
							configurations.
						</p>
						<div style={{ margin: "15px 0", display: "flex", alignItems: "center", gap: "15px" }}>
							<span>Target Account:</span>
							<div style={{ minWidth: 200 }}>
								<CustomDropdown
									value={selectedAccount}
									actions={ACCOUNT_DATA.filter(
										(account) => !account.disabledForOrigin || account.disabledForOrigin !== window.location.origin
									)}
									bestOption="Select Account"
									onChange={handleAccountChange}
								/>
							</div>
						</div>
						<Button
							onClick={handleMigration}
							loading={isMigrating}
							disabled={!selectedAccount || isMigrating || !data.disabled}>
							Migrate Account Data
						</Button>
					</>
				)}
			</div>

			{migrationJob && (
				<div
					style={{
						minHeight: 150,
						display: "flex",
						flexDirection: "column",
						justifyContent: "left",
						alignItems: "left",
						marginTop: 30,
						marginBottom: 30,
						padding: 20,
						border: "1px solid var(--ara-border-color, #ccc)",
						borderRadius: 8,
						backgroundColor: "var(--ara-background-secondary, #f9f9f9)",
					}}>
					<h4 className="bs-h4">Active Migration Job ({recordType})</h4>
					<div style={{ margin: "10px 0", fontSize: 14, fontFamily: "monospace" }}>
						<div>
							<strong>Job ID:</strong> {migrationJob.job_id}
						</div>
						<div>
							<strong>Client:</strong> {clientId}
						</div>
						<div>
							<strong>Expires:</strong> {new Date(migrationJob.expiry_time).toLocaleString()}
						</div>
					</div>
					<div style={{ margin: "10px 0" }}>
						<div>
							<strong>Current Status:</strong>
						</div>
						<div style={{ marginLeft: 20, fontSize: 14 }}>
							<div>
								Data Migration:{" "}
								<span
									style={{
										color:
											migrationJob.status.data_migration === "completed"
												? "green"
												: migrationJob.status.data_migration === "failed"
													? "red"
													: "orange",
									}}>
									{migrationJob.status.data_migration}
								</span>
							</div>
							<div>
								File Migration:{" "}
								<span
									style={{
										color:
											migrationJob.status.file_migration === "completed"
												? "green"
												: migrationJob.status.file_migration === "failed"
													? "red"
													: "orange",
									}}>
									{migrationJob.status.file_migration}
								</span>
							</div>
							<div>
								User Migration:{" "}
								<span
									style={{
										color:
											migrationJob.status.user_migration === "completed"
												? "green"
												: migrationJob.status.user_migration === "failed"
													? "red"
													: "orange",
									}}>
									{migrationJob.status.user_migration}
								</span>
							</div>
						</div>
					</div>
					<div style={{ marginTop: 15 }}>
						<Button
							onClick={handleCheckStatus}
							loading={isCheckingStatus}
							disabled={isCheckingStatus}
							style={{ backgroundColor: "var(--primary)", color: "white" }}>
							Check Migration Status
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
